import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const RiskTrendChart = ({ trendData, scenarios }) => {
  const [selectedScenario, setSelectedScenario] = useState('current');

  const getScenarioData = () => {
    return trendData?.[selectedScenario] || trendData?.current;
  };

  return (
    <div className="card bg-gradient-to-br from-card to-primary/5 shadow-xl border-2">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold mb-2 bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">Tren Risiko & Proyeksi</h2>
          <p className="text-sm md:text-base text-muted-foreground">
            Analisis historis dan proyeksi evolusi risiko
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {scenarios?.map((scenario) => (
            <button
              key={scenario?.id}
              onClick={() => setSelectedScenario(scenario?.id)}
              className={`px-5 py-3 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
                selectedScenario === scenario?.id
                  ? 'bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-lg shadow-primary/30'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {scenario?.label}
            </button>
          ))}
        </div>
      </div>
      <div className="w-full h-96 mb-6 p-4 rounded-2xl bg-gradient-to-br from-muted/30 to-transparent border border-border shadow-inner" aria-label="Risk Trend Analysis Chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={getScenarioData()}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="month" 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px', fontWeight: '600' }}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px', fontWeight: '600' }}
              label={{ value: 'Skor Risiko', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'var(--color-card)',
                border: '2px solid var(--color-border)',
                borderRadius: '12px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
              }}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="supply" 
              stroke="var(--color-error)" 
              strokeWidth={3}
              name="Risiko Pasokan"
              dot={{ r: 5, fill: 'var(--color-error)' }}
            />
            <Line 
              type="monotone" 
              dataKey="operational" 
              stroke="var(--color-warning)" 
              strokeWidth={3}
              name="Risiko Operasional"
              dot={{ r: 5, fill: 'var(--color-warning)' }}
            />
            <Line 
              type="monotone" 
              dataKey="financial" 
              stroke="var(--color-accent)" 
              strokeWidth={3}
              name="Risiko Keuangan"
              dot={{ r: 5, fill: 'var(--color-accent)' }}
            />
            <Line 
              type="monotone" 
              dataKey="market" 
              stroke="var(--color-primary)" 
              strokeWidth={3}
              name="Risiko Pasar"
              dot={{ r: 5, fill: 'var(--color-primary)' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-5 rounded-2xl bg-gradient-to-br from-error/20 to-error/5 border-2 border-error/30 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-error to-error/60 flex items-center justify-center shadow-md">
              <Icon name="TrendingUp" size={18} className="text-error-foreground" />
            </div>
            <span className="text-sm font-bold text-error">Tren Pasokan</span>
          </div>
          <div className="text-2xl font-bold data-text text-error drop-shadow-sm">+12%</div>
          <p className="text-xs text-muted-foreground mt-1 font-medium">vs bulan lalu</p>
        </div>

        <div className="p-5 rounded-2xl bg-gradient-to-br from-warning/20 to-warning/5 border-2 border-warning/30 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-warning to-warning/60 flex items-center justify-center shadow-md">
              <Icon name="TrendingDown" size={18} className="text-warning-foreground" />
            </div>
            <span className="text-sm font-bold text-warning">Tren Operasional</span>
          </div>
          <div className="text-2xl font-bold data-text text-warning drop-shadow-sm">-5%</div>
          <p className="text-xs text-muted-foreground mt-1 font-medium">vs bulan lalu</p>
        </div>

        <div className="p-5 rounded-2xl bg-gradient-to-br from-accent/20 to-accent/5 border-2 border-accent/30 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-accent/60 flex items-center justify-center shadow-md">
              <Icon name="TrendingUp" size={18} className="text-accent-foreground" />
            </div>
            <span className="text-sm font-bold text-accent">Tren Keuangan</span>
          </div>
          <div className="text-2xl font-bold data-text text-accent drop-shadow-sm">+8%</div>
          <p className="text-xs text-muted-foreground mt-1 font-medium">vs bulan lalu</p>
        </div>

        <div className="p-5 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border-2 border-primary/30 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-md">
              <Icon name="Minus" size={18} className="text-primary-foreground" />
            </div>
            <span className="text-sm font-bold text-primary">Tren Pasar</span>
          </div>
          <div className="text-2xl font-bold data-text text-primary drop-shadow-sm">0%</div>
          <p className="text-xs text-muted-foreground mt-1 font-medium">vs bulan lalu</p>
        </div>
      </div>
    </div>
  );
};

export default RiskTrendChart;